import {
  ConversationTypeEnum,
  MediaItem,
  ReplyOption
} from "@coxauto-ui/cxm/communications/interfaces";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  ConversationContext,
  ConversationDetailsContext,
  useEmailReply
} from "../../context";
import MessageSendButton from "../message-send-button";
import MessagePreviewButton from "../message-preview-button";
import EmailPreviewModal from "../email-preview-modal";
import { TemplateSelectorModal } from "../template-selector-modal";
import { MessageInput } from "../message-input";
import { MessageSubjectInput } from "../message-subject-input";
import { ChannelValueSelector } from "../channel-value-selector";
import {
  StationeryOptions,
  VehicleCardType
} from "@coxauto-ui/cxm/communications/api";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { PlusButton } from "../components/plus-button";
import { useTemplates } from "../hooks/useTemplates";
import { usePlusButtonMenuItems } from "../hooks/usePlusButtonMenuItems";
import { useInsertAtCursor } from "../hooks/useInsertAtCursor";
import { useVehicleItem } from "../hooks/useVehicleItem";
import {
  VEHICLE_OPTIONS,
  VehicleSelectorModal,
  VehicleTokenType
} from "../vehicle-selector-modal";
import { VehicleApiErrorType } from "@coxauto-ui/cxm/communications/api";
import { CcBccToggles } from "../cc-bcc-toggles";
import { EmailChipsInput } from "../email-chips-input";
import { ReplyAlertsBottomContainer } from "../reply-alerts-bottom-container";
import { MediaLibraryModal } from "../media-library-modal";
import { useMediaLibraryPicker } from "../hooks/useMediaLibraryPicker";
import { buildMediaWidget } from "../../utils/build-media-utils";

interface EmailReplyProps {
  stationeryOptions: StationeryOptions;
  selectedReplyOption: ReplyOption | null;
  messageFailedToSend: boolean;
  messageSentResponseFailedMsg: string;
  isMediaEntitled: boolean;
  onMessageSendFailed: () => void;
  onMessageSendFailedSetMsg: (msg: string) => void;
}

const StyledReplySubjectBottom = styled.div`
  margin-top: 0.5em;
  margin-bottom: 0.5em;
`;

const StyledChannelSelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > *:first-child {
    flex: 1;
  }
`;

const StyledReplyBottom = styled.div`
  display: flex;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const StyledLeftButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export function EmailReply({
  stationeryOptions,
  selectedReplyOption,
  messageFailedToSend,
  messageSentResponseFailedMsg,
  isMediaEntitled,
  onMessageSendFailed,
  onMessageSendFailedSetMsg
}: EmailReplyProps) {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [vehicleInsertError, setVehicleInsertError] = useState<{
    label: string;
    errorType: VehicleApiErrorType;
  } | null>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const {
    "nx.cxm.inbox-show-plus-button": showPlusButton,
    "nx.cxm.inbox-show-media-library": showMediaLibraryFlag
  } = useCxmFlags();

  const { autoLeadId, repairOrderId, autoLeadStatus, leadStatusError } =
    useContext(ConversationContext);
  const {
    replyText,
    replyTextSubject,
    selectedToChannelValue,
    ccRecipients,
    bccRecipients,
    selectedMedia,
    setReplyText,
    setReplyTextSubject,
    setTemplateHead,
    selectedTemplateLoadState,
    setSelectedTemplateLoadState,
    setCcRecipients,
    setBccRecipients,
    setSelectedMedia,
    setEndlinkMediaMap
  } = useEmailReply();
  const { emojiPickerError, signatureInsertError, setSignatureInsertError } =
    useContext(ConversationDetailsContext);

  const hasValidCcEmails = ccRecipients.some(email => email.trim() !== "");
  const hasValidBccEmails = bccRecipients.some(email => email.trim() !== "");

  useEffect(() => {
    if (ccRecipients.length > 0) {
      setShowCc(true);
    }
  }, [ccRecipients.length]);

  useEffect(() => {
    if (bccRecipients.length > 0) {
      setShowBcc(true);
    }
  }, [bccRecipients.length]);

  const handleToggleCc = () => {
    if (showCc && hasValidCcEmails) {
      return;
    }
    const nextShowCc = !showCc;
    setShowCc(nextShowCc);

    if (!nextShowCc && !hasValidCcEmails) {
      setCcRecipients([]);
    }
  };

  const handleToggleBcc = () => {
    if (showBcc && hasValidBccEmails) {
      return;
    }
    const nextShowBcc = !showBcc;
    setShowBcc(nextShowBcc);

    if (!nextShowBcc && !hasValidBccEmails) {
      setBccRecipients([]);
    }
  };

  const {
    templateData,
    isLoadingTemplates,
    isInsertingTemplate,
    showTokenReplacementWarning,
    setShowTokenReplacementWarning,
    insertTemplate
  } = useTemplates({
    setReplyText,
    setReplyTextSubject,
    setTemplateHead,
    selectedTemplateLoadState,
    setSelectedTemplateLoadState
  });

  const {
    menuItems: plusButtonMenuItems,
    isTemplateSelectorOpen,
    setIsTemplateSelectorOpen,
    isVehicleSelectorOpen,
    setIsVehicleSelectorOpen,
    isMediaLibraryOpen,
    setIsMediaLibraryOpen
  } = usePlusButtonMenuItems({
    autoLeadId,
    isEmailConversation: true,
    showMediaItem: showMediaLibraryFlag && isMediaEntitled
  });

  const { insertContent } = useInsertAtCursor({
    replyText,
    setReplyText,
    editorRef,
    isEmailKendoEditor: true
  });

  const { isInserting, insertVehicleCard, insertVehicleBrochure } =
    useVehicleItem({ onInsertContent: insertContent });

  const handleMediaInsert = useCallback(
    (items: { endlinkUrl: string; media: MediaItem[] }[]) => {
      const html = items
        .map(
          ({ endlinkUrl, media }) =>
            `<p></p>${buildMediaWidget(media, endlinkUrl)}<p></p>`
        )
        .join("");
      insertContent(html);
    },
    [insertContent]
  );

  const handleMediaTitleChange = useCallback(
    (endlinkUrl: string, mediaId: string, newTitle: string) => {
      setEndlinkMediaMap(prev => {
        const entry = prev[endlinkUrl];
        if (!entry) return prev;
        const idx = entry.mediaIds.indexOf(mediaId);
        if (idx === -1) return prev;
        const newTitles = [...entry.titles];
        newTitles[idx] = newTitle;
        return { ...prev, [endlinkUrl]: { ...entry, titles: newTitles } };
      });
    },
    [setEndlinkMediaMap]
  );

  const {
    handleMediaSelect,
    isInserting: isMediaInserting,
    endlinkError,
    clearEndlinkError
  } = useMediaLibraryPicker({
    selectedMedia,
    setSelectedMedia,
    setEndlinkMediaMap,
    onInsertContent: handleMediaInsert,
    onComplete: () => setIsMediaLibraryOpen(false)
  });

  const handlePreviewEmail = () => {
    setIsPreviewModalOpen(true);
  };

  const handleVehicleSelect = async (
    optionId: VehicleTokenType,
    cardType?: VehicleCardType
  ) => {
    const label =
      VEHICLE_OPTIONS.find(o => o.id === optionId)?.label ?? optionId;

    if (optionId === VehicleTokenType.VIN_BROCHURE) {
      const result = await insertVehicleBrochure();
      if (result.success) {
        setIsVehicleSelectorOpen(false);
        setVehicleInsertError(null);
      } else if (result.errorType) {
        setIsVehicleSelectorOpen(false);
        setVehicleInsertError({ label, errorType: result.errorType });
      }
    } else if (cardType) {
      const result = await insertVehicleCard(cardType);
      if (result.success) {
        setIsVehicleSelectorOpen(false);
        setVehicleInsertError(null);
      } else if (result.errorType) {
        setIsVehicleSelectorOpen(false);
        setVehicleInsertError({ label, errorType: result.errorType });
      }
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `[handleVehicleSelect] Unhandled vehicle option: ${optionId} (no cardType)`
      );
      setIsVehicleSelectorOpen(false);
    }
  };

  const handleTemplateSelect = async (
    templateId: number,
    isGenAITemplate: boolean
  ) => {
    const success = await insertTemplate(templateId, isGenAITemplate);

    if (success) {
      setIsTemplateSelectorOpen(false);
    }
  };

  return (
    <>
      <StyledChannelSelectorRow>
        <ChannelValueSelector isDisabled={!selectedReplyOption} />
        <CcBccToggles
          disabled={!selectedReplyOption || messageFailedToSend}
          showBcc={showBcc}
          showCc={showCc}
          onToggleBcc={handleToggleBcc}
          onToggleCc={handleToggleCc}
        />
      </StyledChannelSelectorRow>
      {showCc && (
        <StyledReplySubjectBottom>
          <EmailChipsInput
            disabled={!selectedReplyOption || messageFailedToSend}
            label="CC"
            placeholder="Add CC recipients (press Enter, tab, comma, or space)"
            testId="cc-email-chips-input"
            value={ccRecipients}
            onChange={setCcRecipients}
          />
        </StyledReplySubjectBottom>
      )}
      {showBcc && (
        <StyledReplySubjectBottom>
          <EmailChipsInput
            disabled={!selectedReplyOption || messageFailedToSend}
            label="BCC"
            placeholder="Add BCC recipients (press Enter, tab, comma, or space)"
            testId="bcc-email-chips-input"
            value={bccRecipients}
            onChange={setBccRecipients}
          />
        </StyledReplySubjectBottom>
      )}
      <StyledReplySubjectBottom>
        <MessageSubjectInput
          isDisabled={!selectedReplyOption || messageFailedToSend}
        />
      </StyledReplySubjectBottom>
      <StyledReplyBottom>
        <MessageInput
          editorRef={editorRef}
          isDisabled={!selectedReplyOption || messageFailedToSend}
          recalculateDependencies={[showCc, showBcc]}
          stationeryOptions={stationeryOptions}
          onMediaTitleChange={handleMediaTitleChange}
        />
      </StyledReplyBottom>
      <div>
        <ReplyAlertsBottomContainer
          autoLeadId={autoLeadId}
          autoLeadStatus={autoLeadStatus}
          conversationType={ConversationTypeEnum.EMAIL}
          emojiPickerError={emojiPickerError}
          endlinkError={endlinkError}
          leadStatusError={leadStatusError}
          messageFailedToSend={messageFailedToSend}
          messageSentResponseFailedMsg={messageSentResponseFailedMsg}
          repairOrderId={repairOrderId}
          selectedReplyOption={selectedReplyOption}
          showTokenReplacementWarning={showTokenReplacementWarning}
          signatureInsertError={signatureInsertError}
          vehicleInsertError={vehicleInsertError}
          onCloseTokenWarning={() => setShowTokenReplacementWarning(false)}
          onDismissEndlinkError={clearEndlinkError}
          onDismissSignatureError={() => setSignatureInsertError(null)}
          onDismissVehicleError={() => setVehicleInsertError(null)}
        />
        <StyledButtonContainer>
          <StyledLeftButtonGroup>
            {showPlusButton && (
              <PlusButton
                data-testid="reply-plus-button"
                disabled={!selectedReplyOption || messageFailedToSend}
                menuItems={plusButtonMenuItems}
              />
            )}
          </StyledLeftButtonGroup>
          <div style={{ marginLeft: "auto", display: "flex", gap: "0" }}>
            <MessagePreviewButton onPreview={handlePreviewEmail} />
            <MessageSendButton
              isTextAndNoPhoneNumber={false}
              isTextWithRepairOrderOnly={false}
              onMessageSendFailed={onMessageSendFailed}
              onMessageSendFailedSetMsg={onMessageSendFailedSetMsg}
            />
          </div>
        </StyledButtonContainer>
      </div>

      {/* Email preview modal */}
      <EmailPreviewModal
        bccRecipients={bccRecipients}
        ccRecipients={ccRecipients}
        htmlContent={replyText || ""}
        isOpen={isPreviewModalOpen}
        recipient={selectedToChannelValue}
        subject={replyTextSubject || ""}
        onClose={() => setIsPreviewModalOpen(false)}
      />

      {/* Template selector modal */}
      <TemplateSelectorModal
        isInsertingTemplate={isInsertingTemplate}
        isLoading={isLoadingTemplates}
        isOpen={isTemplateSelectorOpen}
        templateData={templateData}
        onClose={() => setIsTemplateSelectorOpen(false)}
        onTemplateSelect={handleTemplateSelect}
      />

      {/* Vehicle selector modal */}
      <VehicleSelectorModal
        isInserting={isInserting}
        isOpen={isVehicleSelectorOpen}
        onClose={() => setIsVehicleSelectorOpen(false)}
        onVehicleSelect={handleVehicleSelect}
      />

      {/* Media library modal */}
      <MediaLibraryModal
        isInserting={isMediaInserting}
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onMediaSelect={handleMediaSelect}
      />
    </>
  );
}

export default EmailReply;
